
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Shield, Clock, CreditCard, Tag, Palette, CheckCircle, MessageSquare } from 'lucide-react';
import { useAuth } from '@/components/auth/AuthProvider';
import { TimeManagementSettings } from './TimeManagementSettings';
import { CreditSettings } from './CreditSettings';

import { TemplateDesigner } from './TemplateDesigner';
import { AdminPhoneSettings } from './AdminPhoneSettings';

interface AdminSettingsProps {
  onBack: () => void;
}

export const AdminSettings = ({ onBack }: AdminSettingsProps) => {
  const { user, profile } = useAuth();
  const [showDesigner, setShowDesigner] = useState(false);

  if (showDesigner) {
    return <TemplateDesigner onBack={() => setShowDesigner(false)} />;
  }

  return (
    <div className="container mx-auto p-4 md:p-6">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" onClick={onBack} className="flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-foreground">Admin Settings</h1>
          <p className="text-muted-foreground">Manage your admin account and system settings</p>
        </div>
      </div>

      <div className="max-w-4xl">
        <Tabs defaultValue="security" className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="security" className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Security
            </TabsTrigger>
            <TabsTrigger value="phone" className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              SMS Admin
            </TabsTrigger>
            <TabsTrigger value="time" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Time Management
            </TabsTrigger>
            <TabsTrigger value="credit" className="flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              Credit Management
            </TabsTrigger>
            <TabsTrigger value="designer" className="flex items-center gap-2">
              <Palette className="w-4 h-4" />
              Label Designer
            </TabsTrigger>
          </TabsList>

          <TabsContent value="security" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Security Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-green-50/50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                    <div>
                      <h4 className="font-medium text-green-800 dark:text-green-200">Secure Authentication Enabled</h4>
                      <p className="text-sm text-green-700 dark:text-green-300">
                        Admin access is now protected by Supabase authentication with role-based access control.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50/50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">Current User</h4>
                  <div className="space-y-1 text-sm text-blue-700 dark:text-blue-300">
                    <p><strong>Email:</strong> {user?.email}</p>
                    <p><strong>Role:</strong> {profile?.role}</p>
                    <p><strong>Status:</strong> Active Admin</p>
                  </div>
                </div>

                <div className="bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                  <h4 className="font-medium text-amber-800 dark:text-amber-200 mb-2">Security Improvements</h4>
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    The old PIN-based authentication system has been replaced with secure Supabase authentication. 
                    All admin functions now require proper user authentication and role verification.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="phone">
            <AdminPhoneSettings />
          </TabsContent>

          <TabsContent value="time">
            <TimeManagementSettings />
          </TabsContent>
          
          <TabsContent value="credit">
            <CreditSettings />
          </TabsContent>

          <TabsContent value="designer">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="w-5 h-5" />
                  Label Template Designer
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Design and manage label templates with custom dimensions and layouts. Set default templates that will be used across the store for printing product labels.
                </p>
                <Button onClick={() => setShowDesigner(true)} className="w-full">
                  Open Template Designer
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
